import React, { useEffect, useState } from 'react';
import { IconButton, Box, Tabs, Tab, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button as MuiButton, CircularProgress, Tooltip } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import PageHeader1 from '../../components/common/PageHeader1';
import axios from 'axios';
import toast from 'react-hot-toast';
import { usePermissions } from '../../context/AuthContext';

function ListConsultationCoupon() {
    const { can } = usePermissions();
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [open, setOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [filterModel, setFilterModel] = useState({
        items: [],
        quickFilterExcludeHiddenColumns: true,
        quickFilterValues: [''],
    });

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [paginationDetail, setPaginationDetail] = useState({ totalDocs: 0, totalPages: 0, page: 1, limit: 10 });

    const getCategoryParam = () => {
        if (tabValue === 0) return 'flat_extra_rupees';
        if (tabValue === 1) return 'flat_extra_percentage';
        return '';
    };

    const fetchCoupons = async (page = 1, limit = 10, category = '') => {
        setLoading(true);
        const token = localStorage.getItem("vedhvidhi-admin-token");

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/coupon?page=${page}&limit=${limit}&category=${category}&type=consultation`,
                { headers: { Authorization: `${token}` } }
            );

            if (response.data.success) {
                setCoupons(response.data.data || []);
                setPaginationDetail(response.data.paginationDetail || { totalDocs: 0, totalPages: 0, page: 1, limit: 10 });
            } else {
                setError('Failed to fetch coupons');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const category = getCategoryParam();
        fetchCoupons(paginationModel.page + 1, paginationModel.pageSize, category);
    }, [paginationModel, tabValue]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const handleClickOpen = (userId) => {
        setSelectedUserId(userId);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setSelectedUserId(null);
    };

    const handleDelete = async () => {
        const token = localStorage.getItem("vedhvidhi-admin-token");
        setLoading(true);
        try {
            await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/coupon/${selectedUserId}`, {
                headers: { Authorization: `${token}` },
            });

            toast.success("Coupon deleted successfully!");
            const category = getCategoryParam();
            fetchCoupons(paginationModel.page + 1, paginationModel.pageSize, category);
        } catch (err) {
            toast.error("Failed to delete coupon.");
        } finally {
            setLoading(false);
            handleClose();
        }
    };

    const formatDateOnly = (dateString) => {
        if (!dateString) return "N/A";
        // Extract date part (YYYY-MM-DD) from ISO string
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
    };

    const rows = coupons.map((item, index) => ({
        id: item._id,
        sNo: index + 1 + paginationModel.page * paginationModel.pageSize,
        couponCode: item?.couponCode,
        category: item?.category,
        discountValue: item?.discountValue,
        validFrom: formatDateOnly(item?.validFrom),
        validTo: formatDateOnly(item?.validTo),
        minCartValue: item?.minCartValue,
        discountType: item.discountType,
        createdAt: item?.createdAt ? new Date(item?.createdAt).toLocaleString() : "",
    }));

    const columns = [
        { field: 'sNo', headerName: 'S.No.', width: 80 },
        { field: 'couponCode', headerName: 'Coupon Code', width: 150 },
        { field: 'category', headerName: 'Category', width: 150 },
        { field: 'discountValue', headerName: 'Discount', width: 150 },
        { field: 'validFrom', headerName: 'Start Date', width: 150 },
        { field: 'validTo', headerName: 'End Date', width: 150 },
        { field: 'minCartValue', headerName: 'Minimum Cart Value', width: 150 },
        { field: 'createdAt', headerName: 'Created At', width: 200 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: ({ row }) => (
                <>
                    {can('consultation_coupons:delete') && (
                        <Tooltip title="Delete Coupon">
                            <IconButton onClick={() => handleClickOpen(row.id)}>
                                <i className="icofont-ui-delete text-danger"></i>
                            </IconButton>
                        </Tooltip>
                    )}
                </>
            ),
        },
    ];

    return (
        <div className="body d-flex py-1">
            <div className="container-xxl">
                <PageHeader1
                    pagetitle='Consultation Coupon List'
                    righttitle='Add Coupon'
                    link='/consultation-add'
                    routebutton={can('consultation_coupons:add')}
                />

                <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="coupon tabs">
                        <Tab label="Flat Rs Extra" />
                        <Tab label="Percent Extra" />
                    </Tabs>
                </Box>

                <div className="row g-0 mb-3">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                {error ? (
                                    <p className="text-danger">{error}</p>
                                ) : (
                                    <Box sx={{ height: 500 }}>
                                        <DataGrid
                                            loading={loading}
                                            columns={columns}
                                            rows={rows}
                                            getRowId={(row) => row.id}
                                            disableColumnFilter
                                            disableDensitySelector
                                            slots={{ toolbar: GridToolbar }}
                                            filterModel={filterModel}
                                            onFilterModelChange={setFilterModel}
                                            slotProps={{ toolbar: { showQuickFilter: true } }}
                                            columnVisibilityModel={columnVisibilityModel}
                                            onColumnVisibilityModelChange={setColumnVisibilityModel}
                                            paginationMode="server"
                                            rowCount={paginationDetail.totalDocs}
                                            pageSizeOptions={[10, 20, 50]}
                                            paginationModel={paginationModel}
                                            onPaginationModelChange={setPaginationModel}
                                        />
                                    </Box>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete this coupon?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleClose}>Cancel</MuiButton>
                    <MuiButton onClick={handleDelete} color="error" disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : "Delete"}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ListConsultationCoupon;